import { lazy, Suspense } from 'react';

import { Stack, Text, useComputedColorScheme } from '@mantine/core';

/*
  The Lottie runtime is ~328 KB -- larger than the rest of the app's own code
  put together -- and this is a loading indicator, so it must never sit in a
  first-paint path. Loading it dynamically keeps it out of every chunk that
  merely *might* show a spinner; the animation swaps in once it arrives.
*/
const DotLottieReact = lazy(() =>
  import('@lottiefiles/dotlottie-react').then((m) => ({ default: m.DotLottieReact }))
);

const css = `
  .bsl-label{font-family:'DM Mono','Courier New',monospace;font-size:.68rem;letter-spacing:.25em;color:var(--assay-ore);text-transform:uppercase;animation:bsl-breathe 2s ease-in-out infinite}
  @keyframes bsl-breathe{0%,100%{opacity:.3}50%{opacity:.85}}
`;

export interface ScaleLoaderProps {
  label?: string;
  isDarkBackground?: boolean;
}

// Atom - rocking balance-scale SVG for full-page or section-level loading.
// Needs a sized parent (it fills 100% width/height). Pair with <Center> for
// full-page usage or a sized <Box> for section-level slots.
//
// Usage - full-page route loading:
//   <Center mih="60vh">
//     <ScaleLoader label="connecting gmail" />
//   </Center>
//
// Usage - section/card slot:
//   <Box style={{ minHeight: rem(240), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//     <ScaleLoader label="loading context" />
//   </Box>
//
// Pass `label={undefined}` (or omit) to hide the caption. For inline/button
// loading use OreLoader instead - this component is too large for those slots.
export function ScaleLoader({ label = 'assaying', isDarkBackground = false }: ScaleLoaderProps) {
  const colorScheme = useComputedColorScheme('dark');

  const lottiePath =
    isDarkBackground || colorScheme === 'dark'
      ? 'https://lottie.host/7dcb2f92-9bed-427c-9b6b-a4542a5299db/4jsPZ2P6Fa.lottie'
      : 'https://lottie.host/53f94e0f-c33c-4a92-ac4b-9b1b333e30f6/6TqfAJMrQF.lottie';

  return (
    <>
      <style>{css}</style>
      <Stack align="center" gap="xs" w="100%">
        <Suspense fallback={null}>
          <DotLottieReact src={lottiePath} loop autoplay />
        </Suspense>
        {label && <Text className="bsl-label">{label}</Text>}
      </Stack>
    </>
  );
}
